import { useEffect, useRef, useState } from 'react'

import type { EditHistoryState, ReplayResult } from '@fit-editor/fit-core'

import { currentActivityAsync } from '@/worker/fit-client'

/**
 * Module-level cache, keyed by `EditHistoryState` object identity, of the
 * in-flight/most-recent `currentActivityAsync` request for a given
 * history. `LoadedActivity.history` is replaced (never mutated) by every
 * store action that changes it (`applyOperationToOneId`, `undo`/`redo`,
 * etc. -- see `activity-store.ts`), so keying on reference is both a valid
 * cache-invalidation signal and exactly the same "same reference = same
 * logical state" assumption `useOperationPreview`'s baseline-entry
 * comparison already relies on.
 *
 * This exists because several independent, simultaneously-mounted
 * components (the dashboard, the workspace's map wrapper, the timeline,
 * the sidebar's list items/detail panel) all need the *same selected
 * activity's* current replayed state during the same render pass -- one
 * `WeakMap` entry means they share a single `currentActivityAsync` worker
 * round trip instead of each independently triggering their own. A
 * `WeakMap` (not a plain `Map`) so entries for histories no longer
 * referenced by the store (replaced by a later edit, or the activity was
 * removed) become eligible for garbage collection on their own, with no
 * explicit cache-eviction logic needed here.
 */
const cache = new WeakMap<EditHistoryState, Promise<ReplayResult>>()

function getOrFetch(history: EditHistoryState): Promise<ReplayResult> {
  let promise = cache.get(history)
  if (promise == null) {
    promise = currentActivityAsync(history)
    cache.set(history, promise)
  }
  return promise
}

export interface CurrentActivityState {
  /** The replayed activity for `history`, or `null` while loading / when `history` is `undefined`. */
  result: ReplayResult | null
  loading: boolean
}

/**
 * Replays `history`'s full operation log up to its cursor off the main
 * thread (via the Worker-routed `currentActivityAsync`), replacing every
 * direct main-thread call to fit-core's `currentActivity` in the UI layer
 * -- see this milestone's handoff report for why that direct call was a
 * main-thread-blocking bug. Mirrors `useOperationPreview`'s own
 * request-id-guarded, "compare the latest fetched entry's identity
 * against the current `history`" pattern, so a stale response from a
 * since-replaced `history` is never shown.
 *
 * Pass `undefined` (e.g. `open ? loaded?.history : undefined` for a
 * dialog that's always mounted but only sometimes visible) to skip
 * fetching entirely -- see `useOperationPreview`'s identical convention
 * for why this matters: every edit dialog in this codebase stays mounted
 * even while closed, so an unconditional fetch here would hit the worker
 * for every such dialog as soon as any activity is selected, not just the
 * one the user actually has open.
 */
export interface CurrentActivityOptions {
  /**
   * While a new `history` is replaying, keep returning the last result
   * instead of `null` (`loading` is still true). Use only where the caller
   * is remounted per activity (e.g. keyed by activity id), so a previous
   * activity's data never shows under a different selection -- the point
   * is to avoid tearing down heavy views (the map) on every edit.
   */
  keepPreviousWhileLoading?: boolean
}

export function useCurrentActivity(
  history: EditHistoryState | undefined,
  options: CurrentActivityOptions = {},
): CurrentActivityState {
  const [entry, setEntry] = useState<{ history: EditHistoryState; result: ReplayResult } | null>(
    null,
  )
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (history == null) return
    const requestId = ++requestIdRef.current
    void getOrFetch(history).then((result) => {
      if (requestIdRef.current === requestId) setEntry({ history, result })
    })
  }, [history])

  const isCurrent = entry != null && entry.history === history
  const keep = options.keepPreviousWhileLoading === true && history != null
  const result = entry != null && (isCurrent || keep) ? entry.result : null
  return { result, loading: history != null && !isCurrent }
}
